import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import DnDFileUpload from "../../../components/MDDForm/components/DnDFileUpload.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "../../../components/MDDForm/components/ContentDialog.jsx";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import { getEuro, getSbLabel } from "@accredo/fe-shared/services/formatService.js";
import {
  downloadCurrentObjektliste,
  downloadObjektlisteTemplate,
  importObjektliste,
} from "@accredo/fe-shared/services/objektImportService.js";

const buildMatchedByLabels = (objectRefNumLabel) => ({
  id: "ID",
  customer_reference_number: objectRefNumLabel || "Ref-ID",
  bezeichnung: "Bezeichnung",
  adresse: "Adresse",
});

const buildFieldLabels = (objectRefNumLabel) => ({
  bezeichnung: "Bezeichnung",
  plz: "PLZ",
  ort: "Ort",
  strasse: "Straße",
  hausnr: "Hausnr.",
  typ: "Typ",
  customer_reference_number: objectRefNumLabel || "Ref-ID",
});

// Spalten der Sparten-Tabelle im Detail-Panel - analog zum Sparten-Panel in
// CustomerContractsTable.jsx, aber ohne Zugang/Abgang (die Import-Zeile liefert
// keine Gültigkeitsdaten je Sparte, siehe ObjektlisteRowMapper::sparteAttributes()).
// "status"/"changes_text" kommen nicht direkt vom Backend, sondern werden erst
// in decorateSparten() aus sparten_changes (ObjektImportPreviewService::diffSparten())
// zugeordnet. "is_new_sparte" kommt dagegen direkt vom Backend (ein Feld je
// Sparte-Rohdatensatz) und ist unabhängig von status: eine Sparte kann bei
// diesem Objekt neu sein (status: "neu"), obwohl sie im globalen Sparte-Katalog
// bereits existiert - und umgekehrt.
const SPARTEN_DETAIL_COLUMNS = [
  {
    accessorKey: "status",
    header: "",
    size: 160,
    Cell: ({ row }) => {
      const { status, is_new_sparte } = row.original;
      if (!status && !is_new_sparte) return null;

      return (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {status && (
            <Chip
              size="small"
              color={status === "neu" ? "success" : "info"}
              label={status === "neu" ? "Neu" : "Geändert"}
            />
          )}
          {is_new_sparte && (
            <Chip size="small" color="warning" variant="outlined" label="Neue Sparte" />
          )}
        </Box>
      );
    },
  },
  { accessorKey: "sparte", header: "Sparte", size: 150 },
  {
    accessorKey: "selbstbehalt",
    header: "Selbstbehalt",
    size: 200,
    Cell: ({ row }) =>
      getSbLabel(
        row.original.sb_prozent,
        row.original.sb_min,
        row.original.sb_max,
        row.original.sb_fix
      ),
  },
  {
    accessorKey: "jnetto",
    header: "Jahresnetto",
    size: 130,
    Cell: ({ row }) => getEuro(row.original.jnetto),
  },
  {
    accessorKey: "jbrutto",
    header: "Jahresbrutto",
    size: 130,
    Cell: ({ row }) => getEuro(row.original.jbrutto),
  },
  { accessorKey: "changes_text", header: "Änderung", size: 320 },
];

// Für eine per diffSparten() als "geaendert" erkannte Sparte einen lesbaren
// Text bauen - Selbstbehalt wird als EIN zusammengefasster Wert dargestellt
// (siehe ObjektImportPreviewService::diffSparten()), nicht Feld für Feld.
function formatSpartenChangeFields(changes) {
  return Object.entries(changes || {})
    .map(([field, [oldValue, newValue]]) => {
      if (field === "selbstbehalt") {
        const oldLabel = getSbLabel(
          oldValue.sb_prozent,
          oldValue.sb_min,
          oldValue.sb_max,
          oldValue.sb_fix
        );
        const newLabel = getSbLabel(
          newValue.sb_prozent,
          newValue.sb_min,
          newValue.sb_max,
          newValue.sb_fix
        );
        return `Selbstbehalt: ${oldLabel} → ${newLabel}`;
      }
      const label = field === "jnetto" ? "Jahresnetto" : "Jahresbrutto";
      return `${label}: ${getEuro(oldValue)} → ${getEuro(newValue)}`;
    })
    .join("; ");
}

// Kompakte Ein-Zeilen-Zusammenfassung aller Sparten-Änderungen einer Zeile,
// für die "Änderungen"-Spalte der Haupttabelle (Tab "Aktualisiert").
function formatSpartenChangesSummary(spartenChanges) {
  return (spartenChanges || [])
    .map((change) =>
      change.status === "neu"
        ? `Sparte „${change.sparte}“ neu`
        : `Sparte „${change.sparte}“: ${formatSpartenChangeFields(change.changes)}`
    )
    .join("; ");
}

// Ordnet die Datei-Sparten (row.sparten, Rohwerte) den erkannten Änderungen
// (row.sparten_changes, nach Sparte-Name) zu, damit das Detail-Panel Status
// und Änderungstext pro Zeile anzeigen kann.
function decorateSparten(sparten, spartenChanges) {
  const changesByName = new Map(
    (spartenChanges || []).map((change) => [change.sparte, change])
  );

  return (sparten || []).map((sparte) => {
    const change = changesByName.get(sparte.sparte);
    return {
      ...sparte,
      status: change?.status,
      changes_text:
        change?.status === "geaendert"
          ? formatSpartenChangeFields(change.changes)
          : "",
    };
  });
}

// Detail-Panel (aufklappbare Zeile) mit den Sparten des Objekts als Tabelle,
// siehe ObjektImportPreviewService::preview() für die Rohstruktur je Sparte.
function renderSpartenDetailPanel({ row }) {
  const sparten = decorateSparten(
    row.original.sparten,
    row.original.sparten_changes
  );

  if (sparten.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        Keine Sparten in der Datei angegeben.
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <DataTable
        columns={SPARTEN_DETAIL_COLUMNS}
        data={sparten}
        hasLoaded
        enableTopToolbar={false}
        enablePagination={false}
        enableColumnFilters={false}
        enableColumnHiding={false}
        enableSorting={false}
      />
    </Box>
  );
}

// Für die einzelnen Änderungen (alt -> neu) je Feld einen lesbaren Text bauen,
// siehe ObjektImportPreviewService::diffAttributes() für die Rohstruktur.
function formatChanges(changes, fieldLabels) {
  return Object.entries(changes || {})
    .map(([field, [oldValue, newValue]]) => {
      const label = fieldLabels[field] ?? field;
      return `${label}: ${oldValue ?? "–"} → ${newValue ?? "–"}`;
    })
    .join("; ");
}

// Kombiniert Objekt-Attribut-Änderungen (changes) und Sparten-Änderungen
// (sparten_changes) zu einer Zeile für die "Änderungen"-Spalte - die volle
// Aufschlüsselung je Sparte liefert das Detail-Panel (renderSpartenDetailPanel()).
function formatAllChanges(entry, fieldLabels) {
  return [
    formatChanges(entry.changes, fieldLabels),
    formatSpartenChangesSummary(entry.sparten_changes),
  ]
    .filter(Boolean)
    .join(" | ");
}

function decoratePreview(result, matchedByLabels, fieldLabels) {
  return {
    ...result,
    to_add: result.to_add.map((entry) => ({
      ...entry,
      matched_by_label: "Neu",
    })),
    to_update: result.to_update.map((entry) => ({
      ...entry,
      matched_by_label: matchedByLabels[entry.matched_by] ?? "–",
      changes_text: formatAllChanges(entry, fieldLabels),
    })),
    unchanged: result.unchanged.map((entry) => ({
      ...entry,
      matched_by_label: matchedByLabels[entry.matched_by] ?? "–",
    })),
    // to_remove ist neu (Synchron-Entfernen, siehe docs/plan/objekt-import-synchron-entfernen.md) -
    // Default [], damit ältere/unvollständige Vorschau-Objekte (z. B. in Tests) nicht brechen.
    to_remove: result.to_remove || [],
  };
}

const buildBaseColumns = (objectRefNumLabel) => [
  { accessorKey: "bezeichnung", header: "Bezeichnung", size: 220 },
  ...(objectRefNumLabel
    ? [
        {
          accessorKey: "customer_reference_number",
          header: objectRefNumLabel,
          size: 100,
        },
      ]
    : []),
  { accessorKey: "strasse", header: "Straße", size: 150 },
  { accessorKey: "hausnr", header: "Hausnr.", size: 90 },
  { accessorKey: "plz", header: "PLZ", size: 80 },
  { accessorKey: "ort", header: "Ort", size: 150 },
  { accessorKey: "typ", header: "Typ", size: 110 },
];

const UNMATCHED_COLUMNS = [
  { accessorKey: "row", header: "Zeile", size: 70 },
  { accessorKey: "reason", header: "Grund", size: 320 },
];

// Eine Tab pro Diff-Kategorie - Reihenfolge bestimmt auch die Priorität bei der
// automatischen Auswahl des zuerst angezeigten Tabs (siehe runPreview()).
// hasSparten: true zeigt pro Zeile ein aufklappbares Detail-Panel mit den
// Sparten der Datei (nicht sinnvoll für "unmatched" - da liegt kein Objekt vor).
const buildPreviewTabs = (objectRefNumLabel) => {
  const baseColumns = buildBaseColumns(objectRefNumLabel);

  return [
    {
      key: "to_add",
      label: "Neu",
      color: "success",
      columns: baseColumns,
      hasSparten: true,
    },
    {
      key: "to_update",
      label: "Aktualisiert",
      color: "info",
      columns: [
        ...baseColumns,
        { accessorKey: "matched_by_label", header: "Gefunden über", size: 130 },
        { accessorKey: "changes_text", header: "Änderungen", size: 300 },
      ],
      hasSparten: true,
    },
    {
      key: "unmatched",
      label: "Fehler",
      color: "warning",
      columns: UNMATCHED_COLUMNS,
      hasSparten: false,
    },
    {
      key: "unchanged",
      label: "Unverändert",
      color: "default",
      columns: [
        ...baseColumns,
        { accessorKey: "matched_by_label", header: "Gefunden über", size: 130 },
      ],
      hasSparten: true,
    },
    {
      key: "to_remove",
      label: "Zu entfernen",
      color: "error",
      columns: baseColumns,
      hasSparten: false,
    },
  ];
};

const STEP = { UPLOAD: 0, PREVIEW: 1, RESULT: 2 };

function buildResultSummary(preview, syncConfirmed) {
  const removedPart =
    syncConfirmed && preview.to_remove.length > 0
      ? `, ${preview.to_remove.length} Objekt(e) entfernt`
      : "";
  const unmatchedPart =
    preview.unmatched.length > 0
      ? `, ${preview.unmatched.length} Zeile(n) übersprungen`
      : "";

  return `${preview.to_add.length} Objekt(e) neu angelegt, ${preview.to_update.length} aktualisiert${removedPart}. ${preview.unchanged.length} waren bereits unverändert${unmatchedPart}.`;
}

function PreviewTable({ entries, columns, renderDetailPanel }) {
  if (!entries || entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Keine Einträge.
      </Typography>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={entries}
      hasLoaded
      renderDetailPanel={renderDetailPanel}
      enableTopToolbar={false}
      enablePagination={false}
      enableColumnFilters={false}
      enableColumnHiding={false}
      enableSorting={false}
      enableStickyHeader
      muiTableContainerProps={{
        sx: {
          maxHeight: 280,
          overflowX: "auto",
          overflowY: "auto",
          borderLeft: 0,
          borderRight: 0,
        },
      }}
    />
  );
}

PreviewTable.propTypes = {
  entries: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessorKey: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
    })
  ).isRequired,
  renderDetailPanel: PropTypes.func,
};

/**
 * XLSX-Objekt-Import-Dialog für einen Vertrag (ACC-318), dreistufiger Ablauf
 * analog BankAccountObjectImportDialog.jsx, aber ohne Spalten-Mapping-Schritt
 * (Objektliste hat ein festes Spaltenformat, siehe ObjektlisteRowMapper):
 * 1. Datei - Upload, Backend berechnet direkt die Dry-Run-Vorschau.
 * 2. Vorschau - Tabs (Neu/Aktualisiert/Fehler/Unverändert). "Zurück" verwirft
 *    die Datei (Schritt 1). "Jetzt importieren" führt den Import sofort aus
 *    und springt zu Schritt 3.
 * 3. Importieren - reine Ergebnis-Bestätigung, keine weitere Aktion mehr
 *    möglich außer "Schließen".
 */
export default function ObjektImportPreviewDialog({
  open,
  onClose,
  vertrag,
  onSuccess,
  objectRefNumLabel,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();

  const previewTabs = useMemo(
    () => buildPreviewTabs(objectRefNumLabel),
    [objectRefNumLabel]
  );
  const matchedByLabels = useMemo(
    () => buildMatchedByLabels(objectRefNumLabel),
    [objectRefNumLabel]
  );
  const fieldLabels = useMemo(
    () => buildFieldLabels(objectRefNumLabel),
    [objectRefNumLabel]
  );

  const [upload, setUpload] = useState([]);
  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState(previewTabs[0].key);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isDownloadingCurrent, setIsDownloadingCurrent] = useState(false);
  const [applied, setApplied] = useState(false);
  const [syncConfirmed, setSyncConfirmed] = useState(false);

  const resetState = () => {
    setUpload([]);
    setPreview(null);
    setApplied(false);
    setSyncConfirmed(false);
  };

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }
    resetState();
    setOpenCD(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vertrag?.id]);

  const runPreview = async (filedata) => {
    setIsPreviewing(true);
    setPreview(null);
    try {
      const result = await importObjektliste(vertrag.id, filedata, {
        dryRun: true,
      });
      const decorated = decoratePreview(result, matchedByLabels, fieldLabels);
      setPreview(decorated);
      // Zeigt standardmäßig die erste Kategorie mit Einträgen an, damit
      // Auffälligkeiten (Fehler) nicht hinter einer leeren "Neu"-Tab
      // versteckt bleiben.
      const first_non_empty = previewTabs.find(
        (tab) => decorated[tab.key]?.length > 0
      );
      setActiveTab(first_non_empty?.key || previewTabs[0].key);
    } catch (error) {
      enqueueSnackbar("Vorschau konnte nicht erstellt werden.", {
        variant: "error",
      });
      console.error("Failed to preview object list import", error);
      setUpload([]);
    } finally {
      setIsPreviewing(false);
    }
  };

  // DnDFileUpload liefert bei multiple=false immer ein Array mit dem einen
  // neu ausgewählten/abgelegten Eintrag, filedata bereits als Base64-Data-URL.
  const handleUploadChange = async (newUpload) => {
    setUpload(newUpload);
    const selected = newUpload[0];
    if (selected?.filedata) {
      await runPreview(selected.filedata);
    }
  };

  const handleApply = async () => {
    const filedata = upload[0]?.filedata;
    if (!filedata) return;
    setIsApplying(true);
    try {
      await importObjektliste(vertrag.id, filedata, {
        dryRun: false,
        sync: syncConfirmed,
      });
      enqueueSnackbar("Objektliste wurde importiert.", { variant: "success" });
      setApplied(true);
      onSuccess?.();
    } catch (error) {
      enqueueSnackbar("Fehler beim Übernehmen des Imports.", {
        variant: "error",
      });
      console.error("Failed to apply object list import", error);
    } finally {
      setIsApplying(false);
    }
  };

  const handleBackToUpload = () => {
    resetState();
  };

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      await downloadObjektlisteTemplate();
    } catch (error) {
      enqueueSnackbar("Beispieldatei konnte nicht heruntergeladen werden.", {
        variant: "error",
      });
      console.error("Failed to download objektliste template", error);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleDownloadCurrent = async () => {
    setIsDownloadingCurrent(true);
    try {
      await downloadCurrentObjektliste(vertrag.id, vertrag.id_ov);
    } catch (error) {
      enqueueSnackbar("Objektliste konnte nicht heruntergeladen werden.", {
        variant: "error",
      });
      console.error("Failed to download current objektliste", error);
    } finally {
      setIsDownloadingCurrent(false);
    }
  };

  const has_preview = !!preview;
  const step = applied
    ? STEP.RESULT
    : has_preview || isPreviewing
      ? STEP.PREVIEW
      : STEP.UPLOAD;
  const has_changes =
    has_preview &&
    (preview.to_add.length > 0 ||
      preview.to_update.length > 0 ||
      (syncConfirmed && preview.to_remove.length > 0));

  const renderUploadStep = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Datei mit fester Spaltenstruktur (typ, bezeichnung, plz, ort, strasse,
        hausnr
        {objectRefNumLabel ? `, ${objectRefNumLabel}` : ""}). Bis zu 10 Sparten
        pro Objekt möglich (sparte1/sb1/jnettobeitrag1/jbruttobeitrag1 bis
        sparte10/…). Objekte werden
        {objectRefNumLabel
          ? ` bevorzugt über ${objectRefNumLabel} gematcht, sonst`
          : ""}{" "}
        über die Bezeichnung (frei vergeben oder aus der Adresse gebildet)
        gematcht. Für Objekte ohne Adresse (z. B. Fahrzeuge, PV-Anlagen) die
        Bezeichnung eintragen und Adressfelder leer lassen.
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Button
          variant="text"
          size="small"
          onClick={handleDownloadTemplate}
          disabled={isDownloadingTemplate}
          sx={{ pl: 0 }}
        >
          {isDownloadingTemplate
            ? "Wird heruntergeladen…"
            : "Beispieldatei herunterladen"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleDownloadCurrent}
          disabled={isDownloadingCurrent}
        >
          {isDownloadingCurrent
            ? "Wird heruntergeladen…"
            : "Aktuelle Objektliste herunterladen"}
        </Button>
      </Box>
      <DnDFileUpload
        upload={upload}
        setUpload={handleUploadChange}
        multiple={false}
        allowedExtensions={["XLSX"]}
        disabled={isPreviewing}
      />
      {isPreviewing && <Spinner />}
    </>
  );

  const renderPreviewStep = () => (
    <>
      {preview?.vertrag && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Import für Kunde <strong>{preview.vertrag.kunde_name || "–"}</strong>,
          Vertrag <strong>{preview.vertrag.vertragsnummer || "–"}</strong>.
          Bitte vor dem Übernehmen prüfen, ob das der richtige Vertrag ist.
        </Alert>
      )}
      {preview?.vertrag?.objekte_auto_import_active && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Objekte dieses Kunden werden automatisch aus OpenViva importiert.
          Durch den automatischen Import können hochgeladene Objekte
          überschrieben oder doppelte Einträge angelegt werden.
        </Alert>
      )}
      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={(_e, value) => setActiveTab(value)}>
            {previewTabs.map((tab) => (
              <Tab
                key={tab.key}
                value={tab.key}
                label={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    {tab.label}
                    <Chip
                      size="small"
                      color={tab.color}
                      label={preview[tab.key].length}
                    />
                  </Box>
                }
              />
            ))}
          </TabList>
        </Box>
        {previewTabs.map((tab) => (
          <TabPanel key={tab.key} value={tab.key} sx={{ px: 0 }}>
            {tab.key === "unmatched" && preview.unmatched.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Diese Zeilen aus der Datei werden beim Import übersprungen.
              </Typography>
            )}
            {tab.key === "to_remove" && preview.to_remove.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Diese Objekte sind diesem Vertrag aktuell zugeordnet, kommen in
                der importierten Datei aber nicht mehr vor.
              </Typography>
            )}
            <PreviewTable
              entries={preview[tab.key]}
              columns={tab.columns}
              renderDetailPanel={
                tab.hasSparten ? renderSpartenDetailPanel : undefined
              }
            />
          </TabPanel>
        ))}
      </TabContext>

      {/* Sync ist nur nach expliziter Bestätigung möglich - die Checkbox
          erläutert genau, was das Entfernen bedeutet. Muss vor "Jetzt
          importieren" entschieden werden, da der Import direkt ausgeführt
          wird. */}
      {preview.to_remove.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={syncConfirmed}
                onChange={(e) => setSyncConfirmed(e.target.checked)}
                disabled={isApplying}
              />
            }
            label={`Synchron importieren: Die ${preview.to_remove.length} Objekte im Tab „Zu entfernen“ werden dabei von diesem Vertrag entfernt (das Objekt selbst bleibt erhalten, nur die Vertrags-Verknüpfung wird aufgehoben).`}
          />
        </Alert>
      )}

      {!has_changes && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Keine Änderungen gegenüber dem aktuellen Stand.
        </Typography>
      )}
    </>
  );

  const renderResultStep = () => (
    <Alert severity="success">{buildResultSummary(preview, syncConfirmed)}</Alert>
  );

  const renderActions = () => {
    switch (step) {
      case STEP.PREVIEW:
        return (
          <>
            <Button
              variant="outlined"
              onClick={handleBackToUpload}
              disabled={isApplying}
            >
              Zurück
            </Button>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!has_changes || isApplying || isPreviewing}
            >
              {isApplying ? "Wird importiert..." : "Jetzt importieren"}
            </Button>
          </>
        );
      case STEP.RESULT:
        return (
          <Button variant="contained" onClick={onClose}>
            Schließen
          </Button>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    setTca({
      title: "Objektliste hochladen",
      maxWidth: "xl",
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {vertrag?.label}
            {/* Die ID steht auch im Dateinamen von "Aktuelle Objektliste
                herunterladen" (objektliste_vertrag_<id_ov>.xlsx) - so lässt
                sich beim Re-Upload die richtige Datei zuordnen. */}
            {vertrag?.id_ov && ` | ID: ${vertrag.id_ov}`}
          </Typography>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Datei</StepLabel>
            </Step>
            <Step>
              <StepLabel>Vorschau</StepLabel>
            </Step>
            <Step>
              <StepLabel>Importieren</StepLabel>
            </Step>
          </Stepper>

          {step === STEP.UPLOAD && renderUploadStep()}
          {isPreviewing && step !== STEP.UPLOAD && <Spinner />}
          {step === STEP.PREVIEW && !isPreviewing && renderPreviewStep()}
          {step === STEP.RESULT && renderResultStep()}
        </Box>
      ),
      actions: (
        <>
          {step !== STEP.RESULT && <Button onClick={onClose}>Abbrechen</Button>}
          {renderActions()}
        </>
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vertrag,
    upload,
    preview,
    activeTab,
    isPreviewing,
    isApplying,
    isDownloadingTemplate,
    applied,
    previewTabs,
    objectRefNumLabel,
    syncConfirmed,
  ]);

  return (
    <ContentDialog
      openCD={openCD}
      setOpenCD={setOpenCD}
      tca={tca}
      onClose={onClose}
    />
  );
}

ObjektImportPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  vertrag: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id_ov: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  objectRefNumLabel: PropTypes.string,
};
